import { __decorate, __metadata } from "tslib";
import { registerDecorator, ValidatorConstraint } from 'class-validator';
import { ConfigService } from '../../config';
export function ConfiguratedValidator(options) {
  return function (object, propertyName) {
    registerDecorator({
      target: object.constructor,
      propertyName: propertyName,
      options,
      constraints: [options],
      validator: ConfiguratedValidatorConstraint
    });
  };
}
/**
 * General issue:
 * class-validator supports injection of constraints by registering a custom container.
 * However, such a custom constraint class is not meant to "wrap" standard validators.
 * All Standard validators are set to work as early-evaluated property decorators.
 * At that time it is impossible to inject stuff.
 *
 * So, a ConfiguratedValidator working with injected dependencies cannot rely on standard property validators.
 * instead, it can use a custom validation function that calls validation functions like isEmail directly.
 *
 * General issue #2:
 * IsOptional decorator works in a special way that cannot be reproduced using custom decorators or injected constraints.
 * Workaround is to allow ConfiguratedValidator to contain a custom isOptional flag, which however only skips ITS OWN validation function.
 */
let ConfiguratedValidatorConstraint = /*#__PURE__*/(() => {
  let ConfiguratedValidatorConstraint = class ConfiguratedValidatorConstraint {
    constructor(config) {
      this.config = config;
      this.dynamicErrorMessage = null;
      if (!config) throw new Error('ConfiguratedValidatorConstraint did not receive ConfigService!');
    }
    validate(propValue, args) {
      const object = args.object;
      const propertyName = args.property;
      this.dynamicErrorMessage = null;
      // check and extract our ConfiguratedValidatorOptions from "constraints" array
      const options = args.constraints[0];
      if (!options?.validate) throw new Error('Invalid constraints[0] data passed to ConfiguratedValidatorConstraint!');
      const enableValidation = options.enabled ? options.enabled(this.config) : true;
      if (!enableValidation) return true;
      if (options.optional) {
        const validationIsOptional = options.optional === true || options.optional(this.config);
        /**
         * while only treating null and undefined as "empty" and thus optional values,
         * most editors require empty string to be treated as optional too.
         * this is to ensure that validation won't error in case user deletes
         * the text from a field, leaving an empty string.
         */
        if (validationIsOptional && !notEmptyCheck(object, propertyName, true)) {
          return true;
        }
      }
      let result = false;
      try {
        result = options.validate(propValue, args, this.config);
      } catch (err) {
        result = "Technischer Fehler bei Validierung: " + err;
      }
      if (typeof result === 'string') {
        this.dynamicErrorMessage = result;
        return false;
      } else {
        return result;
      }
    }
    defaultMessage(args) {
      const zippedConfig = args.constraints[0];
      if (this.dynamicErrorMessage) return this.dynamicErrorMessage;
      if (zippedConfig.message) return typeof zippedConfig.message === 'function' ? zippedConfig.message(args) : zippedConfig.message;
      return "ConfiguratedValidator Validierung fehlgeschlagen";
    }
  };
  ConfiguratedValidatorConstraint = __decorate([ValidatorConstraint({
    async: true
  }), __metadata("design:paramtypes", [ConfigService])], ConfiguratedValidatorConstraint);
  return ConfiguratedValidatorConstraint;
})();
export { ConfiguratedValidatorConstraint };
// a provider object working for both angular and nestjs
export const ConfiguratedValidatorConstraintProvider = {
  provide: ConfiguratedValidatorConstraint,
  deps: [ConfigService],
  inject: [ConfigService],
  useFactory: config => {
    return new ConfiguratedValidatorConstraint(config);
  }
};
const notEmptyCheck = (object, propertyName, treatEmptyStringAsEmpty) => {
  const isEmpty = object[propertyName] === null || object[propertyName] === undefined || treatEmptyStringAsEmpty && object[propertyName] === "";
  return !isEmpty;
};